import { useState } from "react";
import { Send, RefreshCw, Trash2, ShieldCheck, UserCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Card, Pill, inputCls, btnPrimary, errMsg, fmtDate } from "./ui";

function EmptySlot({ n, onUpdated }) {
  const [email, setEmail] = useState("");
  const [branch, setBranch] = useState("");
  const invite = async () => {
    try {
      onUpdated((await api.post("/org/members", { email, branch })).data);
      toast.success(`Invitation sent to ${email}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-4" data-testid={`slot-${n}`}>
      <p className="text-xs font-bold text-slate-400">License {n} · available</p>
      <input type="email" className={`${inputCls} mt-2`} placeholder="dentist@clinic.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`slot-${n}-email`} />
      <div className="flex gap-2 mt-2">
        <input className={inputCls} placeholder="Branch (optional)" value={branch} onChange={(e) => setBranch(e.target.value)} data-testid={`slot-${n}-branch`} />
        <button type="button" className={btnPrimary} disabled={!email} onClick={invite} data-testid={`slot-${n}-send`} title="Send invitation"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function UsedSlot({ n, m, onUpdated }) {
  const [replacing, setReplacing] = useState(false);
  const [email, setEmail] = useState("");
  const active = m.status === "active";
  const remove = async () => {
    if (!window.confirm(`Remove ${m.email}? Their institutional access ends now and the license becomes available.`)) return;
    try { onUpdated((await api.delete(`/org/members/${m.id}`)).data); toast.success("License freed"); } catch (e) { toast.error(errMsg(e)); }
  };
  const replace = async () => {
    try { onUpdated((await api.post(`/org/members/${m.id}/replace`, { email, branch: m.branch })).data); toast.success(`License reassigned — invitation sent to ${email}`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-xl border p-4 ${active ? "border-emerald-200 bg-emerald-50/50" : "border-sky-200 bg-sky-50/50"}`} data-testid={`slot-${n}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold text-slate-500">License {n}</p>
        <Pill s={m.status} testid={`slot-${n}-status`} />
      </div>
      <p className="mt-2 font-semibold text-slate-800 flex items-center gap-1.5 min-w-0"><span className="sr-only">{m.email}</span>{active ? <UserCheck className="w-4 h-4 text-emerald-600 shrink-0" /> : <Clock className="w-4 h-4 text-sky-600 shrink-0" />}<span className="truncate" title={m.email}>{m.name || m.email}</span></p>
      <p className="text-xs text-slate-500 truncate" data-testid={`slot-${n}-member`}>{[m.name && m.email, m.branch].filter(Boolean).join(" · ") || "\u00a0"}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{active ? `Active since ${fmtDate(m.activated_at)}` : `Invited ${fmtDate(m.invited_at)} — awaiting acceptance`}</p>
      {replacing ? (
        <div className="flex gap-2 mt-3">
          <input type="email" className={inputCls} placeholder="New dentist email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`slot-${n}-replace-email`} />
          <button type="button" className={btnPrimary} disabled={!email} onClick={replace} data-testid={`slot-${n}-replace-send`}><Send className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="flex gap-3 mt-3 text-xs font-semibold">
          <button type="button" className="text-slate-600 hover:text-[#1C6FE3] flex items-center gap-1" onClick={() => setReplacing(true)} data-testid={`slot-${n}-replace`}><RefreshCw className="w-3.5 h-3.5" /> Replace</button>
          <button type="button" className="text-red-600 hover:text-red-700 flex items-center gap-1" onClick={remove} data-testid={`slot-${n}-remove`}><Trash2 className="w-3.5 h-3.5" /> Remove</button>
        </div>
      )}
    </div>
  );
}

function Summary({ s }) {
  return (
    <Card data-testid="org-seats">
      <div className="flex flex-wrap justify-between text-sm gap-2"><span className="font-semibold text-[#0B2545]">License usage</span>
        <span className="text-slate-500" data-testid="org-seats-summary">{s.active} active · {s.invited} invited · {s.available} available of {s.licenses}</span></div>
      <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden flex">
        <div className="bg-emerald-500" style={{ width: `${s.licenses ? (s.active / s.licenses) * 100 : 0}%` }} />
        <div className="bg-sky-400" style={{ width: `${s.licenses ? (s.invited / s.licenses) * 100 : 0}%` }} />
      </div>
      <p className="text-xs text-slate-500 mt-2">Each invitation is emailed by DentiCision. The dentist signs in (or registers with the same email) and accepts it from My Account.</p>
    </Card>
  );
}

export function OrgTeam({ org, onUpdated }) {
  const canInvite = ["active", "grace", "scheduled"].includes(org.contract?.status);
  const used = org.members.filter((m) => ["active", "invited"].includes(m.status)).sort((a, b) => ((a.slot_at || a.invited_at) < (b.slot_at || b.invited_at) ? -1 : 1));
  const past = org.members.filter((m) => !["active", "invited"].includes(m.status));
  const total = Math.max(org.seats.licenses, used.length);
  return (
    <div className="space-y-5" data-testid="org-team">
      {org.contract && <Summary s={org.seats} />}
      {canInvite ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="license-slots">
          {Array.from({ length: total }, (_, i) => (used[i] ? <UsedSlot key={used[i].id} n={i + 1} m={used[i]} onUpdated={onUpdated} /> : <EmptySlot key={`e${i}`} n={i + 1} onUpdated={onUpdated} />))}
        </div>
      ) : <Card><p className="text-sm text-slate-600">Your license slots appear here once your contract is active.</p></Card>}
      {past.length > 0 && (
        <Card data-testid="org-member-history">
          <p className="font-heading font-semibold text-[#0B2545]">Previous dentists</p>
          <ul className="mt-2 divide-y divide-slate-100 text-sm">{past.map((m) => (
            <li key={m.id} className="py-2 flex justify-between gap-3"><span>{m.email}</span><span className="flex items-center gap-2 text-xs text-slate-500">{fmtDate(m.removed_at || m.invited_at)} <Pill s={m.status} /></span></li>
          ))}</ul>
        </Card>
      )}
      <p className="text-xs text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Each dentist uses their own DentiCision account. Their clinical cases and patient data stay private to them.</p>
    </div>
  );
}
